import { useState } from 'react';
import { useApolloClient, useLazyQuery } from '@apollo/client';
import {
  checkDeviceAssignableQuery,
  CheckDeviceAssignableData,
  RescueDeviceFilter,
  rescueDevicesQuery,
  RescueDevicesData,
  rescueUnassignedDeviceInventoryQuery,
  RescueUnassignedDeviceInventoryData,
} from '../lib/rescueQueries';

const REASSIGN_LOOKUP_FILTERS: RescueDeviceFilter[] = [
  'IN_RESCUE',
  'IN_FOSTER',
  'PENDING_ADOPTIONS',
  'COMPLETED_ADOPTIONS',
];
const BLOCKED_SOURCE_FILTERS: ReadonlySet<RescueDeviceFilter> = new Set([
  'IN_FOSTER',
  'PENDING_ADOPTIONS',
  'COMPLETED_ADOPTIONS',
]);
// The backend's reassignDevice refuses sources with an active rescue transfer
// (foster) or active adoption, so we surface a per-stage instruction instead
// of dead-ending the user at a raw mutation error after they confirm.
const BLOCKED_SOURCE_GUIDANCE: Record<string, { stage: string; action: string }> = {
  IN_FOSTER: { stage: 'in a foster placement', action: 'End the foster placement before reassigning the device.' },
  PENDING_ADOPTIONS: {
    stage: 'pending adoption',
    action: 'Cancel or recall the adoption before reassigning the device.',
  },
  COMPLETED_ADOPTIONS: {
    stage: 'adopted out',
    action: 'Cancel or recall the adoption before reassigning the device.',
  },
};

export type PreAssignVerdict =
  | { type: 'available' }
  | { type: 'reassign'; petName: string }
  | { type: 'blocked'; message: string };

interface CheckArgs {
  moduleId: string;
  currentPetId?: string;
}

/**
 * Gate `assignDevice` against silently stealing a device off another household's
 * pet or dead-ending at a raw backend error. Runs three checks in sequence:
 * the rescue's tracked unassigned inventory, the rescue's currently-attached
 * pets (so we can warn before reassigning), and finally the backend
 * assignability check.
 */
export function usePreAssignDeviceCheck() {
  const apolloClient = useApolloClient();
  const [lookupInventory, { loading: checkingInventory }] = useLazyQuery<RescueUnassignedDeviceInventoryData>(
    rescueUnassignedDeviceInventoryQuery,
    { fetchPolicy: 'network-only' },
  );
  const [checkAssignable, { loading: checkingAssignable }] = useLazyQuery<CheckDeviceAssignableData>(
    checkDeviceAssignableQuery,
    { fetchPolicy: 'network-only' },
  );
  const [checkingMatch, setCheckingMatch] = useState(false);

  const checking = checkingInventory || checkingAssignable || checkingMatch;

  async function check({ moduleId, currentPetId }: CheckArgs): Promise<PreAssignVerdict> {
    const { data: inventoryData, error: inventoryError } = await lookupInventory({
      variables: { input: { query: moduleId, limit: 50 } },
    });
    if (inventoryError) {
      return { type: 'blocked', message: inventoryError.message || 'Failed to verify device inventory' };
    }
    const inInventory = (inventoryData?.rescueUnassignedDeviceInventory?.items ?? []).some(
      (item) => item.moduleId.toUpperCase() === moduleId,
    );
    if (inInventory) {
      return { type: 'available' };
    }

    setCheckingMatch(true);
    let match: { filter: RescueDeviceFilter; petName: string } | null = null;
    try {
      const results = await Promise.all(
        REASSIGN_LOOKUP_FILTERS.map((filter) =>
          apolloClient
            .query<RescueDevicesData>({
              query: rescueDevicesQuery,
              variables: { input: { filter, query: moduleId, limit: 5 } },
              fetchPolicy: 'network-only',
            })
            .then((r) => ({ filter, items: r.data?.rescueDevices?.items ?? [] })),
        ),
      );
      for (const { filter, items } of results) {
        const found = items.find(
          (item) => item.pet.id !== currentPetId && item.pet.device?.moduleId.toUpperCase() === moduleId,
        );
        if (found) {
          match = { filter, petName: found.pet.name };
          break;
        }
      }
    } finally {
      setCheckingMatch(false);
    }
    if (match && BLOCKED_SOURCE_FILTERS.has(match.filter)) {
      const guidance = BLOCKED_SOURCE_GUIDANCE[match.filter];
      return {
        type: 'blocked',
        message: `Device ${moduleId} is currently on ${match.petName}, who is ${guidance.stage}. ${guidance.action}`,
      };
    }
    if (match) {
      return { type: 'reassign', petName: match.petName };
    }

    const { data: assignableData, error: assignableError } = await checkAssignable({ variables: { moduleId } });
    if (assignableError) {
      return { type: 'blocked', message: assignableError.message || 'Failed to verify device availability' };
    }
    const status = assignableData?.checkDeviceAssignable;
    if (!status?.assignable) {
      return {
        type: 'blocked',
        message: status?.reason
          ? `Device ${moduleId} cannot be assigned: ${status.reason}.`
          : `Device ${moduleId} is not available to assign.`,
      };
    }
    return { type: 'available' };
  }

  return { check, checking };
}
